import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';
import { schoolYearKeyFromDate } from '@shared/utils/schoolCalendar';
import type { ModuleKey, SchoolSettings } from '@shared/types';

export interface UseActiveSchoolYearResult {
  activeYear: string;
  globalYear: string;
  supportedYears: string[];
  moduleYearOverrides: Partial<Record<ModuleKey, string>>;
  isOverridden: boolean;
  loading: boolean;
  settings: SchoolSettings | null;
  /**
   * Грешка при четенето на настройките — `null`, когато всичко е наред.
   *
   * 🔴 Изложена, а не само `console.error`-ната: без настройки годината пада на резерва от
   * часовника, тоест таблото продължава да работи и изглежда нормално, докато показва
   * ЧУЖДА година. Мълчаливият резерв е по-опасен от празен екран — екранът кара човека да
   * попита, а годината, която изглежда правдоподобно, не.
   */
  error: Error | null;
}

function normalizeSchoolYear(y: string): string {
  return y.replace('/', '-');
}

export function useActiveSchoolYear(module?: ModuleKey): UseActiveSchoolYearResult {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;
  const [settings, setSettings] = useState<SchoolSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const ref = doc(db, 'schools', schoolId, 'settings', 'general');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSettings(snap.exists() ? (snap.data() as SchoolSettings) : null);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('useActiveSchoolYear: snapshot error', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [schoolId]);

  return useMemo<UseActiveSchoolYearResult>(() => {
    // 🔴 Резервът е ИЗВЕДЕН от днешната дата, не закована константа. Дотук тук стоеше
    // `'2025-2026'`, връщана при всяко липсващо/недостъпно `settings` — тоест при всеки
    // първи render и при всяка грешка на snapshot-а админският календар се заковаваше за
    // 2025-2026, и с всяка изминала година резервът ставаше по-грешен, без нищо да го каже.
    const fallbackYear = schoolYearKeyFromDate(new Date());
    // Празният низ е РЕАЛЕН случай: `saveSchoolSettings` записва документа с
    // `activeSchoolYear: ''`, ако първият запис е дошъл без нея. Без тази проверка
    // `normalizeSchoolYear('')` дава `''` и всяка заявка по година се връща празна.
    const storedYear = settings?.activeSchoolYear
      ? normalizeSchoolYear(settings.activeSchoolYear)
      : '';
    const globalYear = storedYear || fallbackYear;
    const override = module ? settings?.moduleYearOverrides?.[module] : undefined;
    const activeYear = override ? normalizeSchoolYear(override) : globalYear;
    return {
      activeYear,
      globalYear,
      supportedYears: settings?.supportedSchoolYears?.length
        ? settings.supportedSchoolYears
        : [globalYear],
      moduleYearOverrides: settings?.moduleYearOverrides ?? {},
      isOverridden: Boolean(override) && override !== globalYear,
      loading,
      settings,
      error,
    };
  }, [settings, loading, module, error]);
}
